import React from 'react';
import styled from 'styled-components';

type ColorProps = {
  backgroundColor: string;
  border: string;
  fontColor: string;
  hover: {
    fontColor?: string;
    backgroundColor: string;
    borderColor: string;
    boxShadow: string;
  };
};

type StyledButtonProps = {
  themeColors: ColorProps;
  size: {
    fontSize: string;
    padding: string;
  };
};

type ButtonProps = {
  children: React.ReactNode;
  theme?: 'blue' | 'green' | 'orange' | 'red';
  disabled?: boolean;
  onClick?: () => Promise<void> | void | undefined;
  type?: 'button' | 'submit';
  size?: 'big' | 'medium' | 'small';
  variant?: 'primary' | 'secondary';
};

const Button = ({
  children,
  type = 'button',
  variant = 'primary',
  theme = 'orange',
  size = 'medium',
  onClick,
  disabled = false,
}: ButtonProps) => {
  const colors: {
    [variant: string]: {
      [color: string]: ColorProps;
    };
  } = {
    primary: {
      blue: {
        fontColor: '#fff',
        backgroundColor: '#3a58de',
        border: 'none',
        hover: {
          backgroundColor: '#2343d1',
          borderColor: 'inherit',
          boxShadow: 'rgba(30, 56, 174, 0.3) 0 1px 3px 1px',
        },
      },
      green: {
        fontColor: '#fff',
        backgroundColor: '#42c67f',
        border: 'none',
        hover: {
          backgroundColor: '#34ad6c',
          borderColor: 'inherit',
          boxShadow: 'rgba(42, 141, 88, 0.3) 0 1px 3px 1px',
        },
      },
      orange: {
        fontColor: '#fff',
        backgroundColor: '#fc8e49',
        border: 'none',
        hover: {
          backgroundColor: '#fc7522',
          borderColor: 'inherit',
          boxShadow: 'rgba(241, 83, 3, 0.3) 0 1px 3px 1px',
        },
      },
      red: {
        fontColor: '#fff',
        backgroundColor: '#ff6f6f',
        border: 'none',
        hover: {
          backgroundColor: '#ff4747',
          borderColor: 'inherit',
          boxShadow: 'rgba(255, 89, 31, 0.3) 0 1px 3px 1px',
        },
      },
    },
    secondary: {
      blue: {
        fontColor: '#3a58de',
        backgroundColor: 'transparent',
        border: '0.0625rem solid #3a58de',
        hover: {
          fontColor: '#2343d1',
          backgroundColor: 'transparent',
          borderColor: '#2343d1',
          boxShadow: 'rgba(30, 56, 174, 0.3) 0 1px 3px 1px',
        },
      },
      green: {
        fontColor: '#42c67f',
        backgroundColor: 'transparent',
        border: '0.0625rem solid #42c67f',
        hover: {
          fontColor: '#34ad6c',
          backgroundColor: 'transparent',
          borderColor: '#34ad6c',
          boxShadow: 'rgba(42, 141, 88, 0.3) 0 1px 3px 1px',
        },
      },
      orange: {
        fontColor: '#fc8e49',
        backgroundColor: 'transparent',
        border: '0.0625rem solid #fc8e49',
        hover: {
          fontColor: '#fc7522',
          backgroundColor: 'transparent',
          borderColor: '#fc7522',
          boxShadow: 'rgba(241, 83, 3, 0.3) 0 1px 3px 1px',
        },
      },
      red: {
        fontColor: '#ff6f6f',
        backgroundColor: 'transparent',
        border: '0.0625rem solid #ff6f6f',
        hover: {
          fontColor: '#ff4747',
          backgroundColor: 'transparent',
          borderColor: '#ff4747',
          boxShadow: 'rgba(255, 89, 31, 0.3) 0 1px 3px 1px',
        },
      },
    },
  };

  const sizes: {
    [size: string]: {
      padding: string;
      fontSize: string;
    };
  } = {
    medium: {
      padding: '0.5rem 0.75rem',
      fontSize: '1rem',
    },
    small: {
      padding: '0.25rem 0.5rem',
      fontSize: '0.75rem',
    },
  };

  const handleClick = () => {
    onClick && onClick();
  };

  return (
    <StyledButton
      type={type}
      themeColors={colors[variant][theme]}
      size={sizes[size]}
      disabled={disabled}
      onClick={handleClick}
    >
      {children}
    </StyledButton>
  );
};

export default Button;

const StyledButton = styled.button<StyledButtonProps>`
  display: block;
  border-radius: 0.25rem;
  line-height: 1;
  border: none;
  font-size: ${({ size }) => size.fontSize};
  color: ${({ themeColors }) => themeColors.fontColor};
  background-color: ${({ themeColors }) => themeColors.backgroundColor};
  border: ${({ themeColors }) => themeColors.border};
  padding: ${({ size }) => size.padding};
  cursor: pointer;
  transition: all 0.3s ease-in-out 0s;

  &:hover {
    color: ${({ themeColors }) => themeColors.hover.fontColor};
    background-color: ${({ themeColors }) => themeColors.hover.backgroundColor};
    border-color: ${({ themeColors }) => themeColors.hover.borderColor};
    box-shadow: ${({ themeColors }) => themeColors.hover.boxShadow};
  }

  &:disabled {
    opacity: 0.5;
    pointer-events: none;
  }
`;
